/**
 * abilityBaseDamage — an ability's base damage for an attacker (plan 29 F2,
 * lane BS 2026-10-11; split out of CombatLogic.ts).
 *
 * The ONE number the server's `calculateDamage` starts from — before the
 * class and level matchup, crit, school, ±20% variance and armour — and the
 * one the ability tooltip shows (src/shared/content/abilityText.ts).
 */
import { calcAbilityDamage, getAbilityData } from '../content/AbilityDataAdapter';
import type { CombatStats } from './CombatLogic';

// Class base damage adjustments
const CLASS_BASE_DAMAGE: Record<string, number> = {
  'warrior': 5,    // Higher physical damage
  'mage': 3,       // Lower base, but abilities scale better
  'hunter': 4,     // Balanced
  'paladin': 4     // Balanced with defensive capabilities
};

// Ability damage bonuses (placeholder for future expansion)
const ABILITY_DAMAGE_BONUSES: Record<string, number> = {
  'fireball': 15,
  'slash': 10,
  'arrow_shot': 12
};

/**
 * Calculate base damage based on attacker stats
 */
export function calculateBaseDamage(
  attackerLevel: number,
  attackerClass: string,
  abilityId?: string,
  attackStat?: number,
): number {
  // Use TOML-driven damage when ability has data in the content pipeline
  if (abilityId) {
    const abilityData = getAbilityData(abilityId);
    if (abilityData?.baseDamage != null) {
      const stat = attackStat ?? 0;
      return calcAbilityDamage(abilityId, stat, attackerLevel);
    }
  }

  // Fallback: legacy formula for abilities without TOML data
  const levelBonus = attackerLevel * 2;
  const classBase = CLASS_BASE_DAMAGE[attackerClass] || 0;
  const abilityBonus = abilityId ? (ABILITY_DAMAGE_BONUSES[abilityId] || 0) : 0;

  return 10 + levelBonus + classBase + abilityBonus;
}

/** True when the ability's school is not physical (magicAttack scales it, magicDefense mitigates it). */
export function isMagicalAbility(abilityId?: string): boolean {
  const school = abilityId ? getAbilityData(abilityId)?.school : undefined;
  return !!school && school !== 'physical';
}

/** The attacker stat an ability scales with: magicAttack for a magical school, else attack. */
export function abilityAttackStat(
  attacker: Pick<CombatStats, 'attack' | 'magicAttack'>,
  abilityId?: string,
): number {
  return isMagicalAbility(abilityId) ? (attacker.magicAttack ?? attacker.attack ?? 0) : (attacker.attack ?? 0);
}

/**
 * An ability's base damage for this attacker, before the target: class and
 * level matchup, crit, school, variance (±20%) and armour. The ONE number the
 * server's `calculateDamage` starts from and the ability tooltip shows
 * (plan 29 F2, src/shared/content/abilityText.ts).
 */
export function abilityBaseDamage(
  attacker: Pick<CombatStats, 'level' | 'heroClass' | 'attack' | 'magicAttack'>,
  abilityId?: string,
): number {
  return calculateBaseDamage(attacker.level, attacker.heroClass || 'warrior', abilityId, abilityAttackStat(attacker, abilityId));
}

